/* ============================================================
   GLOW — PDP editorial layer (glow-pdp.js)
   Currently: the application-steps carousel (pdp-application-carousel).
   Loaded (deferred) only by sections that need JS. The track is a
   CSS scroll-snap rail, so swipe works before/without JS — this
   script only adds dots, thumbnails, arrows, and keyboard paging
   (controls stay hidden until .is-ready lands on the root).
   Mirrors the glow-stiletto.js bootstrap: idempotent per-element
   init + re-init on Shopify theme-editor section events.
   ============================================================ */
(function () {
  'use strict';

  /* The section emits one deferred <script> tag per instance;
     guard so a second execution can't stack duplicate listeners. */
  if (window.__glowPdpInit) return;
  window.__glowPdpInit = true;

  function initApplicationCarousel(root) {
    if (root.__pdpCarouselBound) return;
    root.__pdpCarouselBound = true;

    var track = root.querySelector('[data-pdp-carousel-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-pdp-carousel-slide]'));
    if (slides.length < 2) return;

    var prev = root.querySelector('[data-pdp-carousel-prev]');
    var next = root.querySelector('[data-pdp-carousel-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-carousel-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-carousel-thumb]'));
    var current = 0;

    function reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function setActive(index) {
      if (index < 0 || index >= slides.length || index === current && root.__pdpCarouselReady) return;
      current = index;
      dots.forEach(function (dot, i) {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      thumbs.forEach(function (thumb, i) {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    function goTo(index) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({
        left: slides[index].offsetLeft - slides[0].offsetLeft,
        behavior: reducedMotion() ? 'auto' : 'smooth'
      });
      setActive(index);
    }

    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); });
    });
    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
    });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    /* Track the slide in view (swipe included): deterministic
       index from scroll position, rAF-throttled. (IO thresholds
       misreport when two slides are mostly visible at once.) */
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth;
        if (step > 0) setActive(Math.round(track.scrollLeft / step));
      });
    }, { passive: true });

    setActive(0);
    root.__pdpCarouselReady = true;
    root.classList.add('is-ready');
  }

  /* Ambient section video: autoplay markup does the work; this just
     pauses off-screen and resumes in view (house UGC-reel behavior). */
  function initAmbientVideo(root) {
    if (root.__pdpVideoBound) return;
    root.__pdpVideoBound = true;

    var video = root.querySelector('video');
    if (!video || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.35 });
    observer.observe(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pdp-carousel]').forEach(initApplicationCarousel);
    (scope || document).querySelectorAll('[data-pdp-video]').forEach(initAmbientVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: re-init when sections are loaded/reordered */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function (e) { initAll(e.target); });
})();
